import React, { useState, useEffect } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

import { getImagePath } from '../config/imagePath';

import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, CUSTOM_LABEL, QS_COLOR, CUSTOM_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

import { holsterData } from '../data/AceVRData'; 
import '../App.css';

// WooCommerce product ID for the ACE VR holster
const PRODUCT_ID = 290402;

const BASE_PRICE = 75.99;

// Derived once from static data. Both hand options always render — selecting
// Left with no left-hand models shows an explanatory notice instead of an empty
// dropdown. Clear a gun's no_left_hand flag and the notice disappears on its own.
const LEFT_HAND_AVAILABLE = holsterData.guns.some(g => !g.no_left_hand);

function AceVRBuilder() {
  const [viewSide, setViewSide] = useState("front"); 
  const [handId, setHandId] = useState("right");
  const [brandId, setBrandId] = useState(""); 
  const [gunId, setGunId] = useState("");
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  
  const [thumbScrewId, setThumbScrewId] = useState("no");
  const [rushService, setRushService] = useState(false);

  const [tacBeltColor, setTacBeltColor] = useState("");
  const [tacBeltSize, setTacBeltSize] = useState("");
  const [fulcrumColor, setFulcrumColor] = useState("");
  const [thighBandColor, setThighBandColor] = useState("");

  const {
    getConfigFromURL, generateShareableURL, saveToLocalStorage, loadFromLocalStorage,
    savedConfigs, saveNamedConfig, deleteNamedConfig, copyToClipboard, copySuccess,
    shareModalOpen, setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, brandId, gunId, colorId, mountId, 
    thumbScrewId, rushService,
    tacBeltColor, tacBeltSize, fulcrumColor, thighBandColor
  });

  const loadConfig = (incomingData) => {
    if (!incomingData || incomingData.type === 'click' || incomingData._reactName) return;

    try {
        const config = incomingData.config ? incomingData.config : incomingData;

        setHandId(config.handId === 'left' ? 'left' : 'right');

        const brandValid = config.brandId && holsterData.brands.some(b => b.id === config.brandId);
        setBrandId(brandValid ? config.brandId : '');

        const gunValid = config.gunId && holsterData.guns.some(g => g.id === config.gunId);
        setGunId(gunValid ? config.gunId : '');

        setColorId(config.colorId || '');
        setMountId(config.mountId || '');

        setThumbScrewId(config.thumbScrewId || 'no');
        setRushService(config.rushService === true);

        setTacBeltColor(config.tacBeltColor || "");
        setTacBeltSize(config.tacBeltSize || "");
        setFulcrumColor(config.fulcrumColor || "");
        setThighBandColor(config.thighBandColor || "");

        setShareModalOpen(false);
    } catch (error) {
        console.error("Error loading config", error);
    }
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  // Only one platform in the catalog — preselect it rather than forcing a
  // dead click through a single-option dropdown.
  useEffect(() => {
    if (holsterData.brands.length === 1) {
      setBrandId(holsterData.brands[0].id);
    }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [handId, brandId, gunId, colorId, mountId, thumbScrewId, rushService, tacBeltColor, tacBeltSize, fulcrumColor, thighBandColor]);

  useEffect(() => {
    const needsTacBeltOptions = 
        mountId === 'tac_belt_standard' || 
        mountId === 'dropleg_qls' || 
        mountId === 'dropleg_no_qls';

    if (!needsTacBeltOptions) {
        setTacBeltColor("");
        setTacBeltSize("");
    }

    const needsDroplegOptions = 
        mountId === 'dropleg_qls' || 
        mountId === 'dropleg_no_qls';

    if (!needsDroplegOptions) {
        setFulcrumColor("");
        setThighBandColor("");
    }
  }, [mountId]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  const availableGuns = brandId 
    ? holsterData.guns.filter(g => {
        if (g.brand_id !== brandId) return false;
        if (handId === "left" && g.no_left_hand) return false;
        return true;
      })
    : [];

  const selectedGun = holsterData.guns.find(g => g.id === gunId);
  const selectedColor = holsterData.colors.find(c => c.id === colorId);
  const selectedMount = holsterData.mounts.find(m => m.id === mountId);
  const selectedThumbScrew = holsterData.thumb_screws?.find(t => t.id === thumbScrewId);

  const selectedTacColor = holsterData.tac_belt_colors.find(c => c.id === tacBeltColor);
  const selectedTacSize = holsterData.tac_belt_sizes.find(s => s.id === tacBeltSize);
  const selectedFulcrum = holsterData.fulcrum_colors.find(c => c.id === fulcrumColor);
  const selectedThigh = holsterData.thigh_band_colors.find(c => c.id === thighBandColor);


  // ==========================================
  // SMART WARNINGS (NEW)
  // ==========================================
  let expertWarning = null;
  // If you need to add custom notes for specific models in the future, 
  // you can set them here like:
  // if (gunId === "springfield_trp_vr") { expertWarning = "NOTE: ..."; }


  // --- LEAD TIME LOGIC ---
  const calculateLeadTime = () => {
    if (!selectedGun || !handId) return { text: "Select Options", date: "...", type: "PENDING" };

    let isQuickShip = false;
    if (handId === "right" && selectedGun.qs_nlb_rh) isQuickShip = true;
    if (handId === "left" && selectedGun.qs_nlb_lh) isQuickShip = true;

    let targetDays = CUSTOM_DAYS;
    if (isQuickShip) {
        targetDays = QS_DAYS;
    } else if (rushService) {
        targetDays = RUSH_DAYS;
    }
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isQuickShip) {
        return { type: QS_LABEL, timeline: QS_TIMELINE_TEXT, date: dateString, color: QS_COLOR, bg: leadTimeTint(QS_COLOR), isCustom: false };
    } 
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return { type: "RUSH PRIORITY", timeline: RUSH_TIMELINE_TEXT, date: dateString, color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR), isCustom: true };
    }
    return { type: CUSTOM_LABEL, timeline: CUSTOM_TIMELINE_TEXT, date: dateString, color: CUSTOM_COLOR, bg: leadTimeTint(CUSTOM_COLOR), isCustom: true };
  };

  const leadTimeInfo = calculateLeadTime();

  // --- AUTO-CLEAR RUSH WHEN NOT APPLICABLE ---
  useEffect(() => {
      if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
          setRushService(false);
      }
  }, [gunId, handId]);

  const subtotal = 
    BASE_PRICE +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedTacColor?.price || 0) +
    (selectedTacSize?.price || 0) +
    (selectedFulcrum?.price || 0) +
    (selectedThigh?.price || 0) +
    (selectedThumbScrew?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  let subOptionsValid = true;
  if (mountId === 'tac_belt_standard' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
      if (!tacBeltColor || !tacBeltSize) subOptionsValid = false;
  }
  if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
      if (!fulcrumColor || !thighBandColor) subOptionsValid = false;
  }

  let isReady = brandId && gunId && colorId && mountId && subOptionsValid;

  const handleAddToCart = () => {
    if (!isReady) return;
    
    let mountDetails = selectedMount?.name;
    if (mountId === 'tac_belt_standard' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        mountDetails += `\n   - Belt Mount Color: ${selectedTacColor?.name}`;
        mountDetails += `\n   - Belt Mount Size: ${selectedTacSize?.name}`;
    }
    if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        mountDetails += `\n   - Fulcrum Color: ${selectedFulcrum?.name}`;
        mountDetails += `\n   - Thigh Band: ${selectedThigh?.name}`;
    }

    const configuration = [
        `Handset Model: ${selectedGun?.name}`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        `Color: ${selectedColor?.name}`,
        `Mount: ${mountDetails}`,
        optionLine("Thumb Screws", selectedThumbScrew?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    const params = new URLSearchParams();
    params.append('add-to-cart', PRODUCT_ID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);
    window.location.href = `/checkout/?${params.toString()}`;
  };

  const getSystemImages = () => {
    let images = [];
    if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        const suffix = mountId === 'dropleg_no_qls' ? '_no_qls' : '';
        if (tacBeltColor) {
            images.push(getImagePath(`mounts/owb_systems/tacbelt_${tacBeltColor}${suffix}_front.png`));
        }
        if (fulcrumColor) {
            const activeThigh = thighBandColor || "black";
            images.push(getImagePath(`mounts/owb_systems/dropleg_${fulcrumColor}_${activeThigh}${suffix}_front.png`));
        }
    }
    return images;
  };

  const systemImageArray = getSystemImages();
  const hasMountSystem = ['dropleg_qls', 'dropleg_no_qls'].includes(mountId);

  const SectionHeader = ({ title, description, imageUrl }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0 }}>{title}</h3>
        {(description || imageUrl) && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">
                    {imageUrl && (
                        <div style={{marginBottom: "10px"}}>
                            <img src={imageUrl} alt="Info" style={{width: "100%", borderRadius: "4px"}} />
                        </div>
                    )}
                    {description}
                </div>
            </div>
        )}
    </div>
  );

  let visualizerMountData = selectedMount;
  if (selectedMount && selectedMount.id === 'tac_belt_standard' && tacBeltColor) {
      visualizerMountData = {
          ...selectedMount,
          img_layer_mount: null,
          img_layer_mount_back: `/images/mounts/tac_belt_standard_${tacBeltColor}_back.png`
      };
  }

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">ACE VR Nidhogg™ Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>Designed for ACE VR Handset</span></h1>
        
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>Purpose-Built for VR:</strong> Designed specifically for ACE VR handset systems.</li>
             <li><strong style={{ color: "#fff" }}>KYDEX® Thermoplastics Construction:</strong> Durable, lightweight, and sweat-resistant for extended training sessions.</li>
             <li><strong style={{ color: "#fff" }}>Precision Fit:</strong> CNC cut and vacuum formed for a secure, rattle-free fit with your ACE VR handset.</li>
             <li><strong style={{ color: "#fff" }}>Mounting Versatility:</strong> Compatible with QLS, RTI, Belt Mounts, and Dropleg systems.</li>
             <li><strong style={{ color: "#fff" }}>Manufactured in New Hampshire:</strong> Materials Sourced in the USA.</li>
           </ul>
        </div>

        <SegmentedControl label="Dominant Hand" options={holsterData.hands} selectedValue={handId} onChange={(val) => { setHandId(val); setGunId(""); }} />

        {handId === "left" && !LEFT_HAND_AVAILABLE ? (
          <div style={{ marginTop: "15px", marginBottom: "20px", padding: "15px", backgroundColor: "#1e1e1e", border: "1px solid #444", borderRadius: "4px", fontSize: "14px", color: "#ccc" }}>
              <strong style={{color: "#D5A678", display: "block", marginBottom: "5px"}}>No Left Handed Models Currently Available</strong>
              <p style={{margin: "0 0 10px 0", fontSize: "13px"}}>Every handset in this builder is currently offered right handed only. Switch to Right Handed to continue, or reach out and we'll let you know when left handed models are released.</p>
              <a href="https://www.anrkydexholsters.com/custom-holster-request/" target="_blank" rel="noopener noreferrer" style={{color: "#D5A678", textDecoration: "underline", fontSize: "13px", fontWeight: "bold"}}>Custom Holster Request | ANR Design Holsters</a>
          </div>
        ) : (
          <>
            <OptionSelector label="Select Platform *" options={holsterData.brands} selectedValue={brandId} onChange={(val) => { setBrandId(val); setGunId(""); }} />
            <OptionSelector label="Handset Model *" options={availableGuns} selectedValue={gunId} onChange={setGunId} disabled={!brandId} />
            
            {/* THIS RENDERS THE SMART WARNING TEXT DYNAMICALLY */}
            {expertWarning && <div style={{marginTop:"15px", marginBottom:"25px", padding:"15px", backgroundColor:"rgba(213, 166, 120, 0.15)", borderLeft:"3px solid #D5A678", color:"#D5A678", fontSize:"13px", lineHeight:"1.5"}}>{expertWarning}</div>}
          </>
        )}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        <SectionHeader title="Color Options" description="ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation." />
        <ColorSelector options={holsterData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <SectionHeader 
                title="Mounting Options" 
                description={
                    <div style={{ textAlign: "left" }}>
                        Choose your preferred mounting hardware for your VR training setup.<br /><br />
                        <a href="https://helpcenter.anrdesign.com/tactical-owb-nidhogg-accessories/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn more about mounting options
                        </a>
                    </div>
                } 
            />
            
            {(mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') && (
                <span style={{ fontSize: "11px", color: "#D5A678", marginBottom: "12px", fontStyle: "italic" }}>
                    (See preview in image)
                </span>
            )}
        </div>
        <OptionSelector label="Select Mount *" options={holsterData.mounts} selectedValue={mountId} onChange={setMountId} />
        
        {selectedMount?.warning && (
          <div style={{
            backgroundColor: "rgba(220, 38, 38, 0.1)",
            border: "1px solid #dc2626",
            borderRadius: "4px",
            padding: "10px 12px",
            marginTop: "8px",
            color: "#dc2626",
            fontSize: "12px",
            fontWeight: "600",
            lineHeight: "1.4"
          }}>
            ⚠️ {selectedMount.warning}
          </div>
        )}

        {(mountId === 'tac_belt_standard' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <SectionHeader title="Tactical Belt Mount Color" description="Select the color for your Tac Belt attachment." />
                <OptionSelector label="Select Color *" options={holsterData.tac_belt_colors} selectedValue={tacBeltColor} onChange={setTacBeltColor} />
                
                <div style={{marginTop: "15px"}}></div>
                <SectionHeader title="Tactical Belt Mount Size" description="Choose the size of your belt loop. Short drops ride higher on the belt." imageUrl="/wp-content/uploads/anr-assets/images/info/tac_belt_sizes.jpg" />
                <OptionSelector label="Select Size *" options={holsterData.tac_belt_sizes} selectedValue={tacBeltSize} onChange={setTacBeltSize} />
            </div>
        )}

        {(mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <SectionHeader title="Fulcrum Color" description="Select the color for the pivoting fulcrum mechanism." imageUrl="/wp-content/uploads/anr-assets/images/info/fulcrum_colors.jpg" />
                <OptionSelector label="Select Fulcrum Color *" options={holsterData.fulcrum_colors} selectedValue={fulcrumColor} onChange={setFulcrumColor} />
                
                <div style={{marginTop: "15px"}}></div>
                <SectionHeader title="Thigh Band Color" description="Select the color for the elastic thigh strap." imageUrl="/wp-content/uploads/anr-assets/images/info/thigh_bands.jpg" />
                <OptionSelector label="Select Thigh Band *" options={holsterData.thigh_band_colors} selectedValue={thighBandColor} onChange={setThighBandColor} />
            </div>
        )}

        <div style={{marginTop: "30px"}}></div>
        <SectionHeader 
            title="Extended Thumb Screws" 
            description="Enjoy on-the-fly adjustability. Extended thumb screws allow for incremental retention adjustments without any tools."
            imageUrl="/wp-content/uploads/anr-assets/images/info/thumb_screws.jpg"
        />
        <SegmentedControl label="" options={holsterData.thumb_screws} selectedValue={thumbScrewId} onChange={setThumbScrewId} />

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                The <strong>ACE VR Holster</strong> is built specifically for the ACE VR handset. 
                Precision CNC cut and vacuum formed from KYDEX® thermoplastics for a secure, rattle-free fit. 
                Compatible with QLS, RTI, Tek-Lok, belt mount, and dropleg mounting systems.
            </p>
            <p style={{ color: "#D5A678", fontWeight: "bold", marginBottom: 0 }}>
                Designed and manufactured in New Hampshire.
            </p>
        </CollapsibleSection>

        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>

      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">

          <HolsterVisualizer 
            holsterType="ace_vr" 
            gunData={selectedGun} 
            colorData={selectedColor} 
            mountData={visualizerMountData}
            thumbScrewData={selectedThumbScrew}
            tacBeltColor={tacBeltColor}
            fulcrumColor={fulcrumColor}
            thighBandColor={thighBandColor}
            isLightbearing={"no"} 
            isLeftHanded={handId === "left"} 
            viewSide={viewSide} 
            systemImage={systemImageArray}
            hasSystemView={hasMountSystem}
          />

          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>
                    {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                        <div style={{ marginLeft: "15px" }}>
                            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", background: rushService ? "#D5A678" : "transparent", border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px", transition: "all 0.2s" }}>
                                <input type="checkbox" checked={rushService} onChange={(e) => setRushService(e.target.checked)} style={{ marginRight: "8px", accentColor: "#000" }} />
                                <div style={{ lineHeight: "1.7" }}><div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div><div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div></div>
                            </label>
                        </div>
                    )}
                </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>${subtotal.toFixed(2)}</span>
                <span style={{ color: "#00ff00" }}>${total}</span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "SELECT OPTIONS"}</button>
        </div>
      </div>
      
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container"><span className="mobile-label">Estimated Total</span><span className="mobile-total">${total}</span></div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "INCOMPLETE"}</button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} shareableURL={shareableURL} currentConfig={getCurrentConfig()} savedConfigs={savedConfigs} onSaveConfig={saveNamedConfig} onLoadConfig={loadConfig} onDeleteConfig={deleteNamedConfig} onCopyLink={copyToClipboard} copySuccess={copySuccess} gunName={selectedGun?.name} colorName={selectedColor?.name} />
    </div>
  );
}

export default AceVRBuilder;